import {
  Description,
  ObliqueDescription,
} from "Features/CheckPrice/Products/Advice/adviceStyled";
import {
  useAdvicePriceCalculationRequests,
  useAdviceResult,
} from "../../hooks/stateSelectors";
import { DownloadContainer, SavingAdvicesMessage } from "./styled";
import {
  AdviceDiffType,
  DiffMessageValues,
  generateDiffMessage,
} from "./utils";

import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { ADVICE_NAMES } from "Store/advice/interfaces";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { usePartnerId } from "Store/partners/partnerHooks";
import { ReduxState } from "Store/rootReducer";
import { TextLanguage } from "Store/texts/interfaces";
import { buttonTrackId } from "Utils/analytics";
import { isDefined } from "Utils/functions";
import { FormikProps } from "formik";
import React from "react";
import { useSelector } from "react-redux";
import { AdvicePriceFormValues } from "../../AdviceCards/interfaces";
import { AdviceDiffConsent } from "./AdviceDiffConsent";
import { DiffMessage } from "./DiffMessage";

interface Props {
  insurances: {
    name: ADVICE_NAMES;
    title: string;
  }[];
  adviceMessagesText: any;
  lang: string;
  formProps: FormikProps<AdvicePriceFormValues>;
  handleTakeScreenshot: (formProps: AdvicePriceFormValues) => void;
  screenshotIsLoading: boolean;
  advicesNotAllowed: boolean;
}
export const AdviceDiffMessage = ({
  insurances,
  adviceMessagesText,
  lang,
  formProps,
  handleTakeScreenshot,
  screenshotIsLoading = false,
  advicesNotAllowed,
}: Props): React.ReactElement => {
  const {
    advice: { advicePriceCalculationResults },
    addProductToCart,
  } = useSelector((state: ReduxState) => state);
  const adviceResult = useAdviceResult();
  const advicePriceCalculationRequests = useAdvicePriceCalculationRequests();
  const isAdvisor = useIsAdvisorSelector();
  const partnerId = usePartnerId();

  const messages: DiffMessageValues[] = insurances
    .map((insurance) =>
      generateDiffMessage(
        isAdvisor,
        partnerId,
        advicePriceCalculationRequests.changedByCustomer,
        insurance,
        adviceMessagesText,
        lang,
        adviceResult?.advice[insurance.name],
      ),
    )
    .filter(isDefined);

  const adviceDiffMessages = adviceMessagesText.adviceMessages;
  const formHasErrors =
    !formProps.isValid && !isDefined(formProps.errors.adviceDiffConsent);

  return (
    <SavingAdvicesMessage className="AdviceDiffCard">
      {!isAdvisor && (
        <>
          <Description fw={700} size="16px" className="AdviceDiffText">
            {adviceDiffMessages.header[lang]}
          </Description>
          <Description fw={400} size="16px" className="AdviceDiffText">
            {adviceDiffMessages.description.desc1[lang]}
          </Description>
          {messages.map(
            ({ insuranceProduct, diffType, message, additionalMessages }) => (
              <div
                key={`${insuranceProduct}_diffMessages`}
                className={`${insuranceProduct}_diffMessages`}
              >
                <DiffMessage
                  key={`${insuranceProduct}_checkedToPurchaseDiff`}
                  diffType={diffType}
                  message={message}
                />
                {additionalMessages.map((additionalMessage, key) => (
                  <DiffMessage
                    key={`${insuranceProduct}_additionalMessage_${key}`}
                    diffType={AdviceDiffType.INFO}
                    message={additionalMessage}
                  />
                ))}
              </div>
            ),
          )}
        </>
      )}
      <AdviceDiffConsent />
      {!advicesNotAllowed && (
        <DownloadContainer
          className="AdviceDownloadContainer"
          withMargin={!isAdvisor}
        >
          <ThemedButton
            type="button"
            id="advice__download-button"
            trackId={buttonTrackId(
              adviceMessagesText.downloadAdvice[TextLanguage.ENGLISH],
            )}
            iconLeft="download"
            variant="text"
            onClick={() => handleTakeScreenshot(formProps.values)}
            disabled={
              advicePriceCalculationResults.isLoading ||
              addProductToCart.isLoading ||
              formHasErrors
            }
          >
            {adviceMessagesText.downloadAdvice[lang]}
          </ThemedButton>
          <ObliqueDescription fw={400} size="16px">
            {isAdvisor
              ? adviceMessagesText.downloadAdvice.descAdvisor[lang]
              : adviceMessagesText.downloadAdvice.desc[lang]}
          </ObliqueDescription>
          {screenshotIsLoading && <ThemedSpinner />}
        </DownloadContainer>
      )}
    </SavingAdvicesMessage>
  );
};
